import { CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { Communication, ContactApi } from '../../../../../../services/api-client/contact-api';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { sortByDateAndStatus } from '../../../../../shared/scheduled-comms/utils/sort-comms';
import { Card, CardTitle, Table } from '../../patient-profile-page.styles';
import { ScheduledCommsTableSkeleton } from './scheduled-comms.skeleton';
import { Container } from './scheduled-comms.styles';

export const ScheduledCommsView = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [comms, setComms] = useState<Communication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    loadScheduledCommunications();
  }, []);

  const loadScheduledCommunications = useCallback(async () => {
    setIsLoading(true);

    try {
      const communications = await contactApi.getScheduledCommunications(contactDetails.id, locationId);

      setComms(sortByDateAndStatus(communications));
    } catch {
      showError('Unable to load scheduled communications.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  const getCommTypeString = (comm: Communication) => {
    switch (comm.type) {
      case 0:
        return 'Email';
      case 1:
      case 2:
        return 'Direct Mail';
      case 3:
      case 4:
        return 'SMS';
      case 5:
        return 'Phone Call';
      default:
        return '';
    }
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <CardTitle>Scheduled Comms</CardTitle>
          {isLoading && <ScheduledCommsTableSkeleton />}
          {!isLoading && comms.length === 0 && <NoDataComponent />}
          {!isLoading && comms.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '15%' }}>Date</TableCell>
                  <TableCell>Communication</TableCell>
                  <TableCell style={{ width: '15%' }}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comms.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>{c.date ? moment(c.date).format('MM/DD/YYYY') : null}</TableCell>
                    <TableCell>{c.note}</TableCell>
                    <TableCell>{getCommTypeString(c)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};
